// @flow

import React, { Component } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { AsYouType } from 'libphonenumber-js';
import moment from 'moment';

import TextInput from './shared/TextInput';
import DateInput from './shared/DateInput';
import Button from './shared/Button';
import Message from './shared/Message';
import Wrapper from './shared/FormFieldWrapper';

import type { UserFormInput } from '../models/ProfileForm';
import type { TextInputErrors } from '../types/form';
import DropdownInput from './shared/DropdownInput';
import authApiService from '../services/auth-api-service';

type Props = {
  user: UserFormInput,
  errors: TextInputErrors,
  onSubmit: (evt: Event) => void,
  onValueChange: (string, string) => void,
  cta?: string,
  successMsg: string,
  btnIsSequential: boolean,
  isLoading: boolean,
  showEmail: boolean,
  disabledInputs: Array,
  emailVerified: boolean,
  email: string,
  phone: string,
  hideInsuranceField?: boolean,
  hidePhoneField?: boolean,
  onlyShowRegionField?: boolean,
  hideRegionField?: boolean
} & WithTranslation;
class UserForm extends Component<Props> {
  state = {
    regions: [],
    loading: true
  };

  fetchAvailableRegions() {
    this.setState({ loading: true });
    authApiService
      .get('/regions')
      .then((response) => {
        const mappedRegions = response.regions.map((region) => {
          return {
            value: region.id,
            label: this.props.t(`regions:${region.code}`)
          };
        });
        this.setState({ regions: [{ label: '' }, ...mappedRegions] });
      })
      .finally(() => {
        this.setState({ loading: false });
      });
  }

  componentDidMount() {
    this.fetchAvailableRegions();
  }

  onChange = (evt: SyntheticInputEvent): void => {
    this.props.onValueChange(evt.target.name, evt.target.value);
  };

  // eslint-disable-next-line no-unused-vars
  onDateChange = (val, name) => {
    const dateString = val && val.isValid() ? val.format('YYYY-MM-DD') : null;
    this.props.onValueChange('dob', dateString);
  };

  handleRawDateChange = (e) => {
    const { value, name } = e.target;
    const date = moment(value);
    const dateString = date && date.isValid() ? date.format('YYYY-MM-DD') : null;
    this.props.onValueChange(name, dateString);
  };

  props: Props;

  render() {
    const {
      user,
      errors,
      disabledInputs,
      successMsg,
      btnIsSequential,
      isLoading,
      cta,
      emailVerified,
      email,
      hideInsuranceField,
      hidePhoneField,
      hideRegionField,
      t,
      onlyShowRegionField
    } = this.props;
    const phoneNumber = new AsYouType('CA').input(user && user.phone);
    return (
      <div className='form-container'>
        <form onSubmit={this.props.onSubmit}>
          {!onlyShowRegionField && (
            <React.Fragment>
              <TextInput
                label={t('userForm:first_name')}
                name='first_name'
                value={user && user.first_name}
                error={errors && errors.first_name}
                placeholder={t('userForm:first_name')}
                onChange={this.onChange}
                required
                disabled={disabledInputs && disabledInputs.includes('FirstName')}
              />
              <TextInput
                label={t('userForm:last_name')}
                name='last_name'
                value={user && user.last_name}
                error={errors && errors.last_name}
                placeholder={t('userForm:last_name')}
                onChange={this.onChange}
                required
                disabled={disabledInputs && disabledInputs.includes('LastName')}
              />
            </React.Fragment>
          )}
          {!hideRegionField && (
            <DropdownInput
              disabled={this.state.loading}
              name='region_id'
              value={user && user.region_id}
              error={errors && errors.region_id}
              onChange={this.onChange}
              selected={user && user.region_id}
              label={t('userForm:region')}
              className='is-dropdown--large'
              options={this.state.regions}
            />
          )}
          {!onlyShowRegionField && !hidePhoneField && (
            <TextInput
              label={t('userForm:phone')}
              placeholder={t('userForm:phone')}
              name='phone'
              value={phoneNumber}
              maxLength='14'
              error={errors && errors.phone}
              onChange={this.onChange}
            />
          )}
          {!onlyShowRegionField && emailVerified === true && (
            <TextInput
              label={t('userForm:email')}
              name='email'
              value={email}
              error={errors && errors.email}
              placeholder={t('userForm:email')}
              onChange={this.onChange}
              required
              disabled={disabledInputs && disabledInputs.includes('Email')}
            />
          )}
          {!onlyShowRegionField && (
            <DateInput
              label={t('userForm:birthdate')}
              name='dob'
              value={user && user.dob}
              error={errors && errors.dob}
              onChange={this.onDateChange}
              onChangeRaw={this.handleRawDateChange}
              required
              disabled={disabledInputs && disabledInputs.includes('Birthdate')}
              dob
            />
          )}
          {!onlyShowRegionField && !hideInsuranceField && (
            <TextInput
              label={t('userForm:health_num_or_similar')}
              name='insurance_number'
              value={user && user.insurance_number}
              error={errors && errors.insurance_number}
              placeholder={t('userForm:health_num_or_similar')}
              onChange={this.onChange}
              required
              disabled={disabledInputs && disabledInputs.includes('HealthNumber')}
            >
              {t('userForm:OHIP_or_equivalent')}
            </TextInput>
          )}
          {this.props.successMsg ? (
            <Wrapper>
              <Message className='is-success'>{successMsg}</Message>
            </Wrapper>
          ) : (
            ''
          )}

          <div className='submit-button-container'>
            <Button
              formSubmit
              className={`is-primary ${btnIsSequential ? 'is-sequential' : ''}`}
              isLoading={isLoading}
              label={cta || t('userForm:cta')}
            />
          </div>
        </form>
      </div>
    );
  }
}

export default withTranslation(['userForm', 'regions'])(UserForm);



// WEBPACK FOOTER //
// ./src/components/user-form.js